import { useState } from 'react'

import { Stack, Tooltip } from '@mui/material'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'

import { CardTitle } from 'components/common/controls'
import {
  isActiveOutOfHoursCharge,
  representsOutOfHours,
} from 'constants/EntityConstants/OtherChargeEntity'
import { useAutomaticBookingChargesSync } from 'hooks'
import { Restricted } from 'providers'
import { TBookingForm } from 'types/form-types'

import ChargeMoreInfoDialog from './ChargeMoreInfoDialog'
import { ChargeTable } from './ChargeTable'
import { ChargeToolbar } from './ChargeToolbar'
import { TChargeToolbarForm } from './ChargeToolbar/chargeToolbar.types'

interface IProps {
  bookingId?: string
  hoverText?: string
  disabled: boolean
  mode?: number
}

const OtherCharges = (props: IProps) => {
  const { bookingId, hoverText = '', disabled, mode } = props
  const [openChargeDescriptionDialog, setOpenChargeDescriptionDialog] = useState(false)
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null)

  const { getValues, setValue, control, resetField, reset, formState } =
    useFormContext<TBookingForm>()
  const { fields, append, update, remove, replace } = useFieldArray({
    name: 'charges',
    keyName: 'chargeKey',
  })

  const { outOfHoursRate, isOutOfHours } = useAutomaticBookingChargesSync({
    control,
    bookingId,
    setValue,
    resetField,
    getValues,
    replace,
    reset,
    isDirty: formState.isDirty,
    mode,
  })

  const handleAddCharge = (formData: TChargeToolbarForm) => {
    const { name, type, rate, vatCategoryId, xeroServiceCode, description } = formData
    const chargesValue = getValues().charges

    const hasOutOfHoursCharge = chargesValue?.some(isActiveOutOfHoursCharge)

    if (representsOutOfHours({ type, name }) && hasOutOfHoursCharge) {
      toast.error('Out Of Hours charge is already applied to this booking.')
      return
    }

    const newRate = rate ? Number(rate) : 1

    let existingChargeIndex
    const existingCharge = chargesValue?.find((charge, index) => {
      existingChargeIndex = index
      return (
        !charge.isDeleted && !charge.isAutomatic && charge.name?.trim() === name?.trim()
      )
    })
    if (existingCharge) {
      setValue(`charges.${existingChargeIndex}.quantity`, Number(existingCharge.quantity) + 1, {
        shouldDirty: true,
      })
      return
    }
    append({
      name,
      type,
      quantity: 1,
      rate: newRate,
      vatCategoryId,
      xeroServiceCode,
      description,
      isAutomatic: false,
      isDeleted: false,
    })
  }

  const handleOnInfoClick = (index: number) => {
    setSelectedFieldIndex(index)
    setOpenChargeDescriptionDialog(true)
  }

  const handleCloseChargeDescriptionDialog = () => {
    setOpenChargeDescriptionDialog(false)
  }

  const handleSaveChargeDescriptionDialog = ({
    vatCategoryId,
    xeroServiceCode,
    description,
  }: {
    vatCategoryId: string
    xeroServiceCode: string
    description: string
  }) => {
    setOpenChargeDescriptionDialog(false)
    if (selectedFieldIndex !== null) {
      setValue(`charges.${selectedFieldIndex}.vatCategoryId`, vatCategoryId, {
        shouldDirty: true,
      })
      setValue(`charges.${selectedFieldIndex}.xeroServiceCode`, xeroServiceCode, {
        shouldDirty: true,
      })
      setValue(`charges.${selectedFieldIndex}.description`, description, { shouldDirty: true })
    }
  }

  return (
    <Tooltip title={disabled ? hoverText : ''} followCursor>
      <div>
        <CardTitle disabled={disabled}>Other Charges</CardTitle>
        <Stack spacing={2}>
          <Restricted to='AddBookingCharge'>
            <ChargeToolbar
              disabled={disabled}
              hasOutOfHoursCharge={!!fields.some(isActiveOutOfHoursCharge)}
              outOfHoursRate={outOfHoursRate}
              onAddCharge={handleAddCharge}
            />
          </Restricted>

          <ChargeTable
            fields={fields}
            isOutOfHours={isOutOfHours}
            update={update}
            remove={remove}
            disabled={disabled}
            onInfoClick={handleOnInfoClick}
          />
        </Stack>

        {openChargeDescriptionDialog && selectedFieldIndex !== null && (
          <ChargeMoreInfoDialog
            selectedFieldIndex={selectedFieldIndex}
            openChargeDescriptionDialog={openChargeDescriptionDialog}
            onCloseDialog={handleCloseChargeDescriptionDialog}
            onSaveClick={handleSaveChargeDescriptionDialog}
          />
        )}
      </div>
    </Tooltip>
  )
}

export default OtherCharges
